/**
 * One school's summary sheet in the chef packet: what gets delivered at that
 * campus, the pile the kitchen packs from and the driver delivers. Abner's
 * target print (Figma 137:1722): "Turn the above into a summary of what gets
 * delivered at each campus. Remove the delivery instructions, keep campus
 * names as a single row. Change subtitles on items row to be the packages
 * description."
 *
 *   OLHSA Pontiac Main Building  Oakland Livingston Human Service Agency   Stop 1 · Thu, Oct 15
 *   Orders · Jeff's Over the Top Catering · Deliveries Thu, Oct 15 · Printed …
 *   Milk  Provide supply for 5 days ──────────────────────────────────────────
 *   ┌ 325x   Milk, Whole, Full Fat, Jug                        6 fl oz per serving ┐
 *   └ Gallon Jug                Chilled  Kitchen [  ] °F  Drop-off [  ] °F  Received [ ] ┘
 *   Breakfast  330 meals · Serve Mon ─────────────────────────────────────────
 *   ┌ 330x   Cheerios, Bulk                                   1/2 cup per serving ┐
 *   └ 3x Bulk Trays w/ 84 / 1x Bulk Tray w/ 78   Ambient  Kitchen [  ] °F  …      ┘
 *
 * Pages fall out of the stack (Hunter, Sep 18), so every page repeats the
 * campus row and the packet, kitchen, day and print time (the `<thead>`). The
 * address and the delivery instructions live on the route sheet. Milk is its
 * own block on top (PROD-1502, Abner 164:875: "Show milk separately on both
 * Summary and Delivery Route, and add text for 'Provide supply for X days'").
 *
 * The sheet travels with the food and is the delivery record (PROD-1453):
 * each row carries the Kitchen and Drop-off °F boxes and a Received box, and
 * the sheet ends on the two sign-offs, the chef at pack-out and school staff
 * at drop-off. Keep the "Kitchen" and "Drop-off" °F labels verbatim:
 * `temp-log-vision.ts` reads them off a photo of this sheet.
 */

import { Fragment } from 'react'

import { PrintDeliverableRow, PrintSummaryRow } from '@/components/chefs/print/print-deliverable-row'
import { PRINT_SECONDARY } from '@/components/chefs/print/print-sheet-heading'
import { PrintHeaderNotice } from '@/components/print/print-header'
import { usePrintedAt } from '@/hooks/chefs/usePrintedAt'
import { campusMilkSupplyDays } from '@/lib/chefs/milk'
import { ordersPrintHeading, sheetPrintSubheading } from '@/lib/chefs/print-identity'
import { milkRowWords, milkSupplyLine } from '@/lib/chefs/print-packages'
import { serveDayBadges } from '@/lib/chefs/serve-day'
import { groupServiceMaterials } from '@/lib/chefs/service-materials'
import { computeMealtimeMeals, sortDeliverables } from '@/lib/chefs/totals'
import type { Campus } from '@/lib/chefs/types'
import { dryRunServiceTypeLabel, MEALTIME_LABEL, MEALTIME_ORDER } from '@/lib/chefs/types'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../../shared/chef-packaging-visibility'

interface PrintCampusSummaryProps {
  campus: Campus
  kitchenName: string
  /** The delivery day this sheet is for. */
  date: Date
  /** The school's place on the route ("Stop 1 · Thu, Oct 15"); null off the route. */
  stopLine?: string | null
}

/** The agency beside the campus name, when it is not the campus's own name. */
export function campusAgency(campus: Pick<Campus, 'name' | 'organizationName'>): string | null {
  const organization = campus.organizationName.trim()
  return organization && organization !== campus.name.trim() ? organization : null
}

/** The line a dry-run sheet carries on every page. A dry run is staged but never delivered; a tasting IS delivered. */
export function campusDryRunWarning(campus: Pick<Campus, 'isDryRun' | 'dryRunServiceType'>): string | null {
  if (!campus.isDryRun) {
    return null
  }
  return campus.dryRunServiceType === 'TASTING'
    ? 'Tasting: deliver as live service'
    : `${dryRunServiceTypeLabel(campus.dryRunServiceType)}: do not deliver`
}

/** A mealtime's heading note: "330 meals", then its serve days ("Serve Mon"), the other-day ones bold. */
export function mealtimeNoteParts(
  meals: number,
  serveDays: readonly string[] | undefined,
): Array<{ text: string; strong: boolean }> {
  return [
    { text: `${meals.toLocaleString()} ${meals === 1 ? 'meal' : 'meals'}`, strong: false },
    ...serveDayBadges(serveDays).map((badge) => ({ text: badge.label, strong: badge.tone !== 'today' })),
  ]
}

/** A section heading on the sheet: the name, and secondary words beside it. */
function SectionHeading({ label, note }: { label: string; note?: ReadonlyArray<{ text: string; strong: boolean }> }) {
  return (
    <tr className="break-after-avoid">
      <td className="border-b border-black pb-1 pt-3">
        <span className="text-[11pt] font-semibold">{label}</span>
        {note && note.length > 0 ? (
          <span className={`ml-2 ${PRINT_SECONDARY}`}>
            {note.map((part, i) => (
              <Fragment key={`${part.text}-${i}`}>
                {i > 0 ? ' · ' : null}
                {part.strong ? <span className="font-semibold text-black">{part.text}</span> : part.text}
              </Fragment>
            ))}
          </span>
        ) : null}
      </td>
    </tr>
  )
}

/** The campus row every page repeats: name and agency on the left, the stop on the right; then the packet line. */
function CampusSheetHeading({
  campus,
  stopLine,
  subheading,
}: {
  campus: Campus
  stopLine?: string | null
  subheading: string
}) {
  const agency = campusAgency(campus)
  const warning = campusDryRunWarning(campus)
  return (
    <>
      <tr>
        <th className="pb-0.5 text-left font-normal">
          <div className="flex items-baseline justify-between gap-4">
            <span className="min-w-0">
              <span className="text-[13pt] font-semibold leading-tight">{campus.name}</span>
              {agency ? <span className={`ml-2 ${PRINT_SECONDARY}`}>{agency}</span> : null}
            </span>
            {stopLine ? <span className="shrink-0 whitespace-nowrap text-[9.5pt]">{stopLine}</span> : null}
          </div>
        </th>
      </tr>
      <tr>
        <th className={`text-left font-normal ${PRINT_SECONDARY} ${warning ? '' : 'pb-2'}`}>
          {subheading}
          <PrintHeaderNotice className="ml-2 inline-block align-middle" />
        </th>
      </tr>
      {warning ? (
        <tr>
          <th className="pb-2 pt-1 text-left">
            <span className="inline-block border border-black px-1.5 py-0.5 text-[9pt] font-semibold uppercase tracking-wide">
              {warning}
            </span>
          </th>
        </tr>
      ) : null}
    </>
  )
}

function WriteInField({ label, className }: { label: string; className?: string }) {
  return (
    <div className={`flex items-end gap-2 ${className ?? ''}`}>
      <span className="whitespace-nowrap">{label}</span>
      <span aria-hidden className="h-5 flex-1 border-b border-black" />
    </div>
  )
}

function StepTitle({ step, title, hint }: { step: number; title: string; hint?: string }) {
  return (
    <p className="flex shrink-0 items-center gap-2">
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-black text-[10px] font-bold">
        {step}
      </span>
      <span className="text-sm font-semibold">{title}</span>
      {hint ? <span className={PRINT_SECONDARY}>{hint}</span> : null}
    </p>
  )
}

/** Step 1 is the chef at pack-out, step 2 is school staff at drop-off; always the sheet's last rows. */
function DeliverySignOff() {
  return (
    <div className="mt-3 flex flex-col gap-2 break-inside-avoid break-before-avoid text-xs">
      <section className="flex items-end gap-6 rounded-lg border border-[#999] px-3 py-2">
        <StepTitle step={1} title="Kitchen sign-out" />
        <WriteInField label="Packed by (Chef):" className="flex-[3]" />
        <WriteInField label="Time out:" className="flex-[2]" />
      </section>
      <section className="flex flex-col gap-3 rounded-lg border-2 border-black px-3 py-2">
        <StepTitle
          step={2}
          title="Drop-off sign-off"
          hint="School staff: fill in each Drop-off °F and Received box above before signing."
        />
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <WriteInField label="Received by (First Name):" />
          <WriteInField label="Signature:" />
          <WriteInField label="Delivered by (Driver):" />
          <div className="flex gap-4">
            <WriteInField label="Date:" className="flex-1" />
            <WriteInField label="Time:" className="flex-1" />
          </div>
          <WriteInField label="Missing / short / out-of-temp items:" className="col-span-2" />
        </div>
      </section>
    </div>
  )
}

// Pagination: the sheet is never held together as a whole (that pushed it to a
// fresh page and left the header alone on page 1). The table splits between
// rows (the browser repeats its <thead>), a heading never ends a page, and the
// sign-off always takes the last rows with it.
export function PrintCampusSummary({ campus, kitchenName, date, stopLine }: PrintCampusSummaryProps) {
  const printedAt = usePrintedAt()
  const ordered = [...campus.mealtimes].sort(
    (a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime),
  )
  // Service materials stay off the page while the chef supplies UI is held (`CHEF_SERVICE_MATERIALS_VISIBLE`).
  const supplyGroups = CHEF_SERVICE_MATERIALS_VISIBLE ? groupServiceMaterials(campus.supplies) : []
  const milk = campus.milk ?? []
  return (
    <div className="font-sans text-[10.5pt] leading-snug text-black">
      <table className="w-full table-fixed border-collapse">
        <thead>
          <CampusSheetHeading
            campus={campus}
            stopLine={stopLine}
            subheading={sheetPrintSubheading(ordersPrintHeading(kitchenName), date, printedAt)}
          />
        </thead>
        {/* Milk first, on its own (PROD-1502): one row per milk item for the delivery. */}
        {milk.length > 0 ? (
          <tbody data-testid="print-milk-block">
            <SectionHeading
              label="Milk"
              note={[{ text: milkSupplyLine(campusMilkSupplyDays(campus)), strong: false }]}
            />
            {milk.map((line) => (
              <tr key={line.key} className="break-inside-avoid">
                <td className="pt-1.5">
                  <PrintSummaryRow words={milkRowWords(line)} />
                </td>
              </tr>
            ))}
          </tbody>
        ) : null}
        {ordered.map((block) => (
          <tbody key={block.mealtime}>
            <SectionHeading
              label={MEALTIME_LABEL[block.mealtime]}
              note={mealtimeNoteParts(computeMealtimeMeals(block), block.serveDays)}
            />
            {/* The menus' own delivery and service notes for this mealtime. */}
            {block.menuInstructions?.length ? (
              <tr className="break-inside-avoid">
                <td className="pb-0.5 pt-1.5 text-[9pt]">
                  {block.menuInstructions.map((note) => (
                    <div key={note.menuId} className="break-inside-avoid">
                      <p className="font-semibold">{note.menuName}</p>
                      {note.delivery ? <p className="whitespace-pre-wrap">Delivery: {note.delivery}</p> : null}
                      {note.service ? <p className="whitespace-pre-wrap">Service: {note.service}</p> : null}
                    </div>
                  ))}
                </td>
              </tr>
            ) : null}
            {/* Order-group sections (PROD-1296): one band per group, each
                headed by its name and its own meal count, so PK and K-8 pack
                apart. */}
            {(block.groups ?? [{ key: 'all', label: '', meals: block.meals, deliverables: block.deliverables }]).map(
              (group) => (
                <Fragment key={group.key}>
                  {block.groups ? (
                    <tr className="break-after-avoid">
                      <td className={`pb-0.5 pt-2 font-semibold uppercase tracking-wide ${PRINT_SECONDARY}`}>
                        {group.label} · {group.meals.toLocaleString()} {group.meals === 1 ? 'meal' : 'meals'}
                      </td>
                    </tr>
                  ) : null}
                  {sortDeliverables(group.deliverables).map((d) => (
                    <tr key={d.id} className="break-inside-avoid">
                      <td className="pt-1.5">
                        <PrintDeliverableRow deliverable={d} />
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ),
            )}
          </tbody>
        ))}
        {supplyGroups.length > 0 ? (
          <tbody>
            <SectionHeading
              label="Supplies"
              note={[{ text: 'Utensils, plates, trays & boats to pull', strong: false }]}
            />
            {supplyGroups.map((group) => (
              <Fragment key={group.kind}>
                <tr className="break-after-avoid">
                  <td className={`pb-0.5 pt-2 font-semibold uppercase tracking-wide ${PRINT_SECONDARY}`}>
                    {group.label}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr
                    key={JSON.stringify([row.skuId, row.isCompostable])}
                    className="break-inside-avoid border-b border-[#ccc]">
                    <td className="py-1">
                      <span className="inline-block w-14 font-semibold tabular-nums">{row.qty.toLocaleString()}x</span>
                      {row.name}
                      {row.isCompostable ? (
                        <span className="ml-1.5 text-[8pt] font-semibold uppercase tracking-wide">Compostable</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        ) : null}
      </table>
      {/* A dry run is staged but never delivered, so it has no drop-off to sign; a tasting IS delivered. */}
      {!campus.isDryRun || campus.dryRunServiceType === 'TASTING' ? <DeliverySignOff /> : null}
    </div>
  )
}
